import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';

const i18n = defineMessages({
	groupOverflowLabel: {
		id: 'team-presence.group.overflow.label',
		defaultMessage: 'Overflow menu',
		description: 'Accessible label for presence overflow menu button',
	},
});

const moreIndicatorButtonBaseStyle = xcss({
	minWidth: '28px',
	padding: 'space.0',
	marginInline: 'space.0',
	marginBlock: 'space.025',
	backgroundColor: 'color.background.neutral.subtle',
	borderRadius: 'radius.small',
	':focus-visible': {
		outlineColor: 'color.border.focused',
		outlineStyle: 'solid',
		outlineWidth: 'border.width.focused',
		outlineOffset: 'space.050',
	},
});

const moreIndicatorStyle = xcss({
	padding: 'space.075',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	font: 'font.heading.xxsmall',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	color: 'color.text.subtle',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	backgroundColor: 'color.background.accent.gray.subtler',
	borderRadius: 'radius.full',
	':hover': {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
		backgroundColor: 'color.background.accent.gray.subtler.hovered',
	},
	':active': {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
		backgroundColor: 'color.background.accent.gray.subtler.pressed',
	},
});

const moreIndicatorActiveStyle = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

export function renderMoreIndicator(isOpen: boolean, count: number) {
	return (_: any, props: any): React.JSX.Element => {
		return <CorePresenceMoreIndicator count={count} isOpen={isOpen} {...props} />;
	};
}

export type CorePresenceMoreIndicatorProps = {
	count: number;
	isOpen: boolean;
	onClick?: () => void;
	testId?: string;
};

export const CorePresenceMoreIndicator = ({
	count,
	isOpen,
	onClick,
	testId,
}: CorePresenceMoreIndicatorProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Pressable
			data-vc="presence-overflow"
			aria-label={formatMessage(i18n.groupOverflowLabel)}
			onClick={onClick}
			testId={testId}
			xcss={moreIndicatorButtonBaseStyle}
		>
			<Box xcss={[moreIndicatorStyle, isOpen && moreIndicatorActiveStyle]}>{count}</Box>
		</Pressable>
	);
};
